<div class="flex flex-col items-center gap-3 w-full">
  <!-- Briefing Notes -->
  @if (!isTemplateSettings()) {
    <div
      class="flex items-center gap-3 w-full rounded-2xl border bg-shikenso-pink-100-12 border-shikenso-pink-100-50 px-3 py-4 mb-3"
    >
      <app-icon-badge
        content="icons/notes.svg"
        backgroundColor="var(--shikenso-base-white)"
        color="var(--shikenso-pink-100)"
        [iconSize]="24"
        [size]="36"
      />

      <div class="flex flex-col flex-1 min-w-0">
        <div class="flex items-center gap-3">
          <span>{{ 'components.briefingNotes.globalTitle' | translate }}</span>
          <span class="text-[10px] text-shikenso-pink-base bg-shikenso-base-white rounded-full px-3">
            {{ 'components.briefingNotes.optional' | translate }}
          </span>
        </div>
        <span class="text-shikenso-purple-base-60 text-xs">
          {{ 'pages.campaignForm.sections.creatorsAndCtas.globalBriefingNotesDescription' | translate }}
        </span>
      </div>
      <button
        mat-flat-button
        type="button"
        color="secondary-white"
        class="mat-slim-plus-button"
        (click)="addGlobalBriefingNotes.emit()"
      >
        <div class="flex items-center gap-1">
          @if (!hasGlobalBriefingNote()) {
            <app-icon src="icons/plus.svg" [size]="16" />
          }

          {{
            hasGlobalBriefingNote()
              ? ('pages.campaignForm.sections.creatorsAndCtas.editNote' | translate)
              : ('pages.campaignForm.sections.creatorsAndCtas.addNote' | translate)
          }}
        </div>
      </button>
    </div>
  }

  <!-- Add Global Deliverables -->
  @if (!isTemplateSettings()) {
    <div class="flex items-center gap-3 w-full">
      <app-icon-badge
        content="icons/world.svg"
        backgroundColor="var(--shikenso-pink-100-12)"
        color="var(--shikenso-purple-base)"
        alt="global deliverables"
        [iconSize]="24"
        [size]="44"
      />

      <div class="flex flex-col flex-1 min-w-0">
        <span class="text-lg">
          {{ 'pages.campaignForm.sections.creatorsAndCtas.globalDeliverables' | translate }}
        </span>
        <span class="text-shikenso-purple-base-60 text-sm">
          {{ 'pages.campaignForm.sections.creatorsAndCtas.globalDeliverablesDescription' | translate }}
        </span>
      </div>

      <button
        mat-flat-button
        type="button"
        color="secondary-white"
        class="mat-slim-plus-button"
        (click)="addGlobalDeliverables.emit()"
      >
        <div class="flex items-center gap-1">
          <app-icon src="icons/plus.svg" [size]="16" />
          {{ 'pages.campaignForm.sections.creatorsAndCtas.addGlobalDeliverables' | translate }}
        </div>
      </button>
    </div>
  } @else {
    <div class="flex items-center gap-3 w-full">
      <div class="flex flex-col flex-1 min-w-0 gap-1">
        <h2>{{ 'pages.settings.templateSettings.title' | translate }}</h2>
        <span class="text-shikenso-purple-base-60 text-sm">
          {{ 'pages.settings.templateSettings.description' | translate }}
        </span>
      </div>

      <button
        mat-flat-button
        type="button"
        color="secondary-white"
        class="mat-slim-plus-button"
        (click)="addGlobalDeliverables.emit()"
      >
        <div class="flex items-center gap-1">
          <app-icon src="icons/plus.svg" [size]="16" />
          {{ 'pages.settings.templateSettings.addGlobalTemplate' | translate }}
        </div>
      </button>
    </div>

    <hr class="border-shikenso-purple-base-12 w-full mt-1" />
  }

  <!-- Count summary badges + expand/collapse all -->
  @if (globalAndSharedCtaValues().length && !loading()) {
    @if (!isTemplateSettings()) {
      <div class="flex items-center justify-between w-full">
        <div class="flex items-center gap-2">
          @if (globalCtaCount() > 0) {
            <span class="text-[10px] bg-shikenso-pink-100-24 text-shikenso-purple-base rounded-full px-2">
              {{
                'pages.campaignForm.sections.creatorsAndCtas.globalGroupsCount' | translate: { count: globalCtaCount() }
              }}
            </span>
          }

          @if (sharedCtaCount() > 0) {
            <span class="text-[10px] bg-shikenso-pink-100-24 text-shikenso-purple-base rounded-full px-2">
              {{
                'pages.campaignForm.sections.creatorsAndCtas.sharedGroupsCount' | translate: { count: sharedCtaCount() }
              }}
            </span>
          }
        </div>

        <button
          mat-flat-button
          type="button"
          color="secondary-gray"
          class="mat-tiny-button"
          (click)="expandCollapseAll()"
        >
          <span class="text-xs">
            {{
              allExpanded()
                ? ('pages.campaignForm.sections.creatorsAndCtas.collapseAll' | translate)
                : ('pages.campaignForm.sections.creatorsAndCtas.expandAll' | translate)
            }}
          </span>
        </button>
      </div>
    }

    <!-- CTA expansion panels -->
    <div
      class="flex flex-col gap-3 w-full"
      [ngClass]="{ 'items-center border border-shikenso-purple-base-12 rounded-3xl py-6': isTemplateSettings() }"
    >
      @for (cta of globalAndSharedCtaValues(); track cta.id) {
        <app-cta-summary-panel
          [ngClass]="{ 'min-w-[676px] max-w-[888px] w-full': isTemplateSettings() }"
          [creatorId]="ALL_CREATORS_ID"
          [cta]="cta"
          [getExistingDeliverablesCount]="getExistingDeliverablesCount()"
          [getExistingExecutionsCount]="getExistingExecutionsCount()"
          [getExistingKpisCount]="getExistingKpisCount()"
          [hideShareStatusIcon]="hideShareStatusIcon()"
          [ctaShareCount]="cta.isAllCreators ? 0 : 2"
          (addDeliverablesForCta)="addDeliverablesForCta.emit($event)"
          (addExecutionsForCta)="addExecutionsForCta.emit($event)"
          (addKpisForCta)="addKpisForCta.emit($event)"
        >
          <div ctaMenu>
            <button mat-icon-button type="button" [matMenuTriggerFor]="globalCtaMenu">
              <app-icon alt="menu" class="w-4 h-4" src="icons/dots.svg" />
            </button>
            <mat-menu #globalCtaMenu="matMenu">
              <button mat-menu-item type="button" (click)="editCta.emit(cta.id!)">
                {{ 'pages.campaignForm.sections.creatorsAndCtas.editName' | translate }}
              </button>
              <button mat-menu-item type="button" (click)="editCtaGroup.emit(cta.id!)">
                {{
                  (cta.isAllCreators
                    ? 'pages.campaignForm.sections.creatorsAndCtas.editGlobalGroup'
                    : 'pages.campaignForm.sections.creatorsAndCtas.editIndividualGroup'
                  ) | translate
                }}
              </button>
              <button mat-menu-item type="button" (click)="copyCta.emit(cta.id!)">
                {{
                  cta.isAllCreators
                    ? ('pages.campaignForm.sections.creatorsAndCtas.copyGlobal' | translate)
                    : ('pages.campaignForm.sections.creatorsAndCtas.copyShared' | translate)
                }}
              </button>
              <button mat-menu-item type="button" (click)="removeCtaGlobally.emit(cta.id!)">
                {{ 'shared.delete' | translate }}
              </button>
            </mat-menu>
          </div>
        </app-cta-summary-panel>
      }
    </div>
  }

  @if (loading()) {
    <div
      class="flex flex-col gap-3 w-full"
      [ngClass]="{ 'items-center border border-shikenso-purple-base-12 rounded-3xl py-6': isTemplateSettings() }"
    >
      @for (i of [1, 2, 3]; track i) {
        <ngx-skeleton-loader
          class="h-[54px] w-full"
          [ngClass]="{ 'min-w-[676px] max-w-[888px] w-full': isTemplateSettings() }"
          [theme]="{ width: '100%', height: '54px', 'border-radius': '16px' }"
        />
      }
    </div>
  }
</div>
